// The field map's "Area" layer, loaded in TILES (2026-10-08, "the assets
// take a long time to load initially, even on wifi").
//
// The phone asks /api/field/area-tiles for slippy-map tiles (z19, ~58 m in
// Wellington) nearest-first: the tiles within 50 m, then 150 m, then the
// Area radius, in batches sized to the connection. Each batch is drawn as it
// arrives and kept (memory + IndexedDB, areaTileStore.ts); walking on only
// fetches the tiles the phone doesn't have yet.
//
// Pure (no DOM / IndexedDB): the frontend unit tests run it under plain Node
// (frontend/tests/areaTiles.test.ts).

export const AREA_TILE_Z = 19;
/** server cap per request (field_assets.AREA_TILES_MAX) */
export const SERVER_MAX_TILES = 128;
/** a request's tiles fit a WINDOW x WINDOW block (the server allows 12) */
export const WINDOW = 12;
/** outer tiles (further than this from the phone) are asked for with ~1 m
 * line simplification (display only) */
export const COARSE_FROM_M = 150;

export interface TileRef {
  x: number;
  y: number;
  /** metres from the phone to the nearest point of the tile */
  d: number;
}

export function tileKey(z: number, x: number, y: number): string {
  return `${z}/${x}/${y}`;
}

export function tileOf(lon: number, lat: number, z: number): [number, number] {
  const n = 2 ** z;
  const x = Math.floor(((lon + 180) / 360) * n);
  const r = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.asinh(Math.tan(r)) / Math.PI) / 2) * n);
  return [Math.min(Math.max(x, 0), n - 1), Math.min(Math.max(y, 0), n - 1)];
}

/** [lon0, lat0, lon1, lat1] (south-west, north-east) */
export function tileBounds(z: number, x: number, y: number): [number, number, number, number] {
  const n = 2 ** z;
  const lat = (yy: number) => (Math.atan(Math.sinh(Math.PI * (1 - (2 * yy) / n))) * 180) / Math.PI;
  return [(x / n) * 360 - 180, lat(y + 1), ((x + 1) / n) * 360 - 180, lat(y)];
}

const M_PER_DEG = 111320;

/** Metres from (lat, lon) to the nearest point of a tile (0 inside it). */
export function tileDistanceM(lat: number, lon: number, z: number, x: number, y: number): number {
  const [lon0, lat0, lon1, lat1] = tileBounds(z, x, y);
  const nx = Math.min(Math.max(lon, lon0), lon1);
  const ny = Math.min(Math.max(lat, lat0), lat1);
  return Math.hypot((nx - lon) * M_PER_DEG * Math.cos((lat * Math.PI) / 180), (ny - lat) * M_PER_DEG);
}

/** Every tile within radiusM of the phone, nearest first. */
export function neededTiles(lat: number, lon: number, radiusM: number, z = AREA_TILE_Z): TileRef[] {
  const [cx, cy] = tileOf(lon, lat, z);
  const [lon0, , lon1] = tileBounds(z, cx, cy);
  const tileM = Math.max(1, (lon1 - lon0) * M_PER_DEG * Math.cos((lat * Math.PI) / 180));
  const k = Math.ceil(radiusM / tileM) + 1;
  const out: TileRef[] = [];
  for (let dx = -k; dx <= k; dx++) {
    for (let dy = -k; dy <= k; dy++) {
      const d = tileDistanceM(lat, lon, z, cx + dx, cy + dy);
      if (d <= radiusM) out.push({ x: cx + dx, y: cy + dy, d });
    }
  }
  out.sort((a, b) => a.d - b.d || a.y - b.y || a.x - b.x);
  return out;
}

// --- connection ---------------------------------------------------------------

export type Tier = "fast" | "medium" | "slow";

/** The bits of navigator.connection we read (Chrome / Android; Safari has none). */
export interface ConnectionInfo {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

export interface Measured {
  /** download throughput (kbit/s), from responses big enough to time */
  kbps: number | null;
  /** time to first byte (ms) */
  rttMs: number | null;
}

export interface LoadPlan {
  tier: Tier;
  /** ring radii in metres; the last one is the Area radius (maybe capped) */
  stages: number[];
  /** tiles per request */
  maxTiles: number;
  /** extra metres loaded beyond the Area radius once it is complete */
  prefetchM: number;
  /** the radius actually loaded (saveData / a slow link caps it) */
  radiusM: number;
  /** why it is capped (for the map hint), else null */
  capped: string | null;
  /** what decided the tier, for the perf log */
  basis: "connection" | "measured" | "default";
}

export function tierOf(conn: ConnectionInfo | null | undefined, m: Measured): { tier: Tier; basis: LoadPlan["basis"] } {
  if (conn?.saveData) return { tier: "slow", basis: "connection" };
  const et = conn?.effectiveType;
  if (et) {
    if (et === "slow-2g" || et === "2g") return { tier: "slow", basis: "connection" };
    if (et === "3g") return { tier: "medium", basis: "connection" };
    // "4g" covers anything from ~1.5 Mbit/s up: refine with downlink
    if (typeof conn?.downlink === "number" && conn.downlink > 0) {
      return { tier: conn.downlink >= 5 ? "fast" : conn.downlink >= 1 ? "medium" : "slow", basis: "connection" };
    }
    return { tier: "fast", basis: "connection" };
  }
  // no Network Information API (iOS): what this session measured
  if (m.kbps !== null || m.rttMs !== null) {
    if ((m.kbps !== null && m.kbps < 700) || (m.rttMs !== null && m.rttMs > 1200)) return { tier: "slow", basis: "measured" };
    if (m.kbps !== null && m.kbps >= 4000 && (m.rttMs === null || m.rttMs < 350)) return { tier: "fast", basis: "measured" };
    return { tier: "medium", basis: "measured" };
  }
  return { tier: "medium", basis: "default" };
}

export function loadPlan(radiusM: number, conn: ConnectionInfo | null | undefined, m: Measured): LoadPlan {
  const { tier, basis } = tierOf(conn, m);
  let r = radiusM;
  let capped: string | null = null;
  if (tier === "slow" && r > 250) {
    r = 250;
    capped = conn?.saveData ? "Data Saver is on" : "the connection is slow";
  }
  // a fast link takes the rest in one stage after the first 50 m (each stage
  // is one server computation; its batches then come from the server's cache)
  const stages = (tier === "fast" ? [50, r] : [50, 150, r]).filter((s, i, a) => s <= r && a.indexOf(s) === i);
  if (stages[stages.length - 1] !== r) stages.push(r);
  return {
    tier,
    basis,
    stages,
    maxTiles: tier === "fast" ? SERVER_MAX_TILES : tier === "medium" ? 32 : 16,
    prefetchM: tier === "fast" ? 100 : 0,
    radiusM: r,
    capped,
  };
}

/** Exponentially weighted throughput / latency from finished requests. */
export class Throughput {
  kbps: number | null = null;
  rttMs: number | null = null;
  /** bytes = bytes on the wire; ms = whole request; ttfbMs = to the first byte */
  add(bytes: number, ms: number, ttfbMs: number | null): void {
    if (ttfbMs !== null && ttfbMs >= 0) this.rttMs = this.rttMs === null ? ttfbMs : 0.6 * this.rttMs + 0.4 * ttfbMs;
    // a small body is all latency: only time downloads worth timing
    const dl = ttfbMs !== null ? ms - ttfbMs : ms;
    if (bytes >= 12_000 && dl > 0) {
      const k = (bytes * 8) / dl; // bits per ms = kbit/s
      this.kbps = this.kbps === null ? k : 0.6 * this.kbps + 0.4 * k;
    }
  }
  get measured(): Measured {
    return { kbps: this.kbps, rttMs: this.rttMs };
  }
}

// --- batches -------------------------------------------------------------------

export interface Batch {
  stage: number;
  tiles: TileRef[];
  /** tiles to simplify (further than COARSE_FROM_M) */
  coarse: TileRef[];
}

/** The requests still to make: the tiles not in `have`, by stage (ring),
 * nearest first. A stage is cut into WINDOW x WINDOW blocks anchored on the
 * phone's tile (the server takes one window per request; a 250 m ring at
 * z19 fits one block), each split at maxTiles; blocks go nearest first. */
export function planBatches(tiles: TileRef[], plan: Pick<LoadPlan, "stages" | "maxTiles">, have: (t: TileRef) => boolean): Batch[] {
  const out: Batch[] = [];
  if (!tiles.length) return out;
  const c = tiles[0]; // the nearest tile (d = 0): the phone's own
  const block = (v: number, c0: number) => Math.floor((v - c0 + WINDOW / 2) / WINDOW);
  let lo = -1;
  for (const stage of plan.stages) {
    const ring = tiles.filter((t) => t.d > lo && t.d <= stage && !have(t));
    lo = stage;
    if (!ring.length) continue;
    const groups = new Map<string, TileRef[]>(); // insertion order = nearest first
    for (const t of ring) {
      const k = `${block(t.x, c.x)}:${block(t.y, c.y)}`;
      const g = groups.get(k);
      if (g) g.push(t);
      else groups.set(k, [t]);
    }
    for (const g of groups.values()) {
      for (let i = 0; i < g.length; i += plan.maxTiles) {
        const part = g.slice(i, i + plan.maxTiles);
        out.push({ stage, tiles: part, coarse: part.filter((t) => t.d > COARSE_FROM_M) });
      }
    }
  }
  return out;
}

// --- merging ---------------------------------------------------------------------

export interface TileData<A, R> {
  assets: A[];
  roads: R[];
}

/** The Area layer from the loaded tiles (each asset is in exactly one tile
 * on the server; deduped anyway, a tile can come from an older fetch). */
export function mergeTiles<A extends { asset_id: string }, R extends { name: string; path: [number, number][] }>(
  tiles: Iterable<TileData<A, R> | undefined>,
): { assets: A[]; roads: R[] } {
  const assets: A[] = [];
  const roads: R[] = [];
  const seen = new Set<string>();
  const seenRoad = new Set<string>();
  for (const t of tiles) {
    if (!t) continue;
    for (const a of t.assets) {
      if (seen.has(a.asset_id)) continue;
      seen.add(a.asset_id);
      assets.push(a);
    }
    for (const r of t.roads) {
      const p = r.path[0];
      const k = `${r.name}|${p ? `${p[0]},${p[1]}` : ""}|${r.path.length}`;
      if (seenRoad.has(k)) continue;
      seenRoad.add(k);
      roads.push(r);
    }
  }
  return { assets, roads };
}

/** Tiles no longer worth keeping a request open for: none of the in-flight
 * tiles is needed any more (the crew walked on). */
export function isStale(inFlight: string[], needed: Set<string>): boolean {
  return inFlight.length > 0 && !inFlight.some((k) => needed.has(k));
}
